import React, { useState, useEffect, useReducer } from "react";
import { Auth } from "aws-amplify";
import { Spinner } from "components";
import UserContext, { UserProvider } from "context/UserContext";
import { ShopProvider } from "context/ShopContext";
import { withRouter } from "react-router-dom";
import { parseQueryString } from "lib/query-string";
import useShop from "hooks/useShop";
import { SetItemReducer, SET_ITEM, SET_ALL } from "reducers/SetItemReducer";
import { GET_SHOP_SETTINGS } from "Queries";
import { SHOP_DEFAULT_SETTINGS } from "lib/globals";
import { SCOPE, APP_DEV } from "aws-exports";
import Axios from "axios";

const Authenticator = ({ children, location }) => {
  const [user, setUser] = useState(null);
  const redirectToLogin = () => {
    const { shop } = parseQueryString(location.search);
    const shopParam = shop ? `?shop=${shop}` : "";
    const url = window.location.origin + "/login" + shopParam;
    if (window.top === window.self) {
      window.location.href = url;
    } else {
      const message = JSON.stringify({
        data: { location: url },
        message: "Shopify.API.remoteRedirect"
      });
      window.parent.postMessage(message, `https://${shop}`);
    }
  };

  const logOutUser = async (shop) => {
    await Auth.signOut();
    return redirectToLogin();
  };

  useEffect(() => {
    async function fetchUser() {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const { shop } = parseQueryString(location.search);

        //in case of multiple stores
        if (currentUser.attributes.website === shop) {
          setUser(currentUser);
        } else {
          await logOutUser(shop);
        }
      } catch (error) {
        return redirectToLogin();
      }
    }
    fetchUser();
  }, []);

  return !user ? (
    <Spinner text="Authentication" />
  ) : (
    <WithShopProvider user={user} logOutUser={logOutUser}>
      <UserProvider value={user}>{children}</UserProvider>
    </WithShopProvider>
  );
};

export default withRouter(Authenticator);

const WithShopProvider = ({ user, children, logOutUser }) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [state, dispatch] = useReducer(SetItemReducer, {});
  const { data, error, loading } = useShop(
    user,
    GET_SHOP_SETTINGS,
    {},
    "cache-first"
  );
  const setItem = (path, value) => dispatch({ type: SET_ITEM, path, value });
  const discard = () => {
    if (data && data?.shop) {
      const { shop } = data;
      dispatch({
        type: SET_ITEM,
        path: "settings",
        value: shop.settings || SHOP_DEFAULT_SETTINGS()
      });
    }
  };
  // const hasRecharge = async (shop_url: string) => {
  //   const { data } = await Axios.get(
  //     `https://static.rechargecdn.com/store/${shop_url}/product/2020-12/products.json`
  //   );
  //   return !!data?.widget_settings;
  // };

  useEffect(() => {
    async function checkScope(shop) {
      // console.log("#################")
      // console.log(shop.scope)
      // console.log(SCOPE)
      // console.log(shop.version)
      // console.log("#################")
      if (shop.scope !== SCOPE || !shop.version || shop.version !== "1.0.3") {
        await logOutUser(shop);
      }
    }
    if (data && data?.shop) {
      const { shop } = data;
      checkScope(shop);
      const default_settings = SHOP_DEFAULT_SETTINGS(
        state.settings ? state.settings.default_language : "english"
      );

      const shop_settings = shop.settings;

      if (
        shop_settings &&
        !shop_settings.modules.checkout_button.coupon_alert_text
      ) {
        shop_settings["modules"]["checkout_button"]["coupon_alert_text"] =
          default_settings["modules"]["checkout_button"]["coupon_alert_text"];
      }
      if (shop_settings && !shop_settings.modules.order_note) {
        shop_settings["modules"]["order_note"] = {
          active: false,
          ...default_settings["modules"]["order_note"]
        };
      }
      if (
        shop_settings &&
        shop_settings["modules"]["order"].indexOf("order_note") === -1
      ) {
        shop_settings["modules"]["order"].push("order_note");
      }
      if (shop_settings && !shop_settings["cod_settings"]) {
        shop_settings["cod_settings"] = default_settings.cod_settings;
      }
      if (shop_settings && !shop_settings.hasOwnProperty("open_cart")) {
        shop_settings["open_cart"] = true;
      }
      if (shop_settings && !shop_settings.hasOwnProperty("show_coupon")) {
        shop_settings["show_coupon"] = true;
      }
      if (
        shop_settings &&
        !shop_settings.hasOwnProperty("show_coupon_alert_text")
      ) {
        shop_settings["show_coupon_alert_text"] = true;
      }
      if (shop_settings && !shop_settings["sticky_cart"]) {
        shop_settings["sticky_cart"] = default_settings.sticky_cart;
      }
      if (shop_settings && !shop_settings["scary"]) {
        shop_settings["scary"] = default_settings.scary;
      }
      if (
        shop_settings &&
        !shop_settings["modules"]["checkout_button"]["continue_shopping_button"]
      ) {
        shop_settings["modules"]["checkout_button"][
          "continue_shopping_button"
        ] = default_settings.modules.checkout_button.continue_shopping_button;
        shop_settings["modules"]["checkout_button"][
          "active_continue_shopping_button"
        ] =
          default_settings.modules.checkout_button.active_continue_shopping_button;
        shop_settings["modules"]["checkout_button"][
          "active_continue_shopping_underlined"
        ] =
          default_settings.modules.checkout_button.active_continue_shopping_underlined;
      }
      if (shop_settings && !shop_settings["checkout_condition_text"]) {
        shop_settings["checkout_condition_text"] =
          default_settings.checkout_condition_text;
        shop_settings["checkout_condition_color"] =
          default_settings.checkout_condition_color;
        shop_settings["is_checkout_condition_active"] = false;
      }

      //check if the store has recharge app if the write_discount scope is not granted
      // let show_recharge_alert = false;
      // if (shop.scope.indexOf("write_discount") === -1 && hasRecharge(shop.id)) {
      //   show_recharge_alert = true;
      // }

      dispatch({
        type: SET_ALL,
        value: {
          ...shop,
          settings: shop_settings || default_settings,
          // settings: default_settings,
          // show_recharge_alert,
          original_settings: shop.settings || {},
          settingsSaved: !!shop.settings ? true : false, //TODO: this will be deprecated
          redirectToChargeApproval: !shop.chargeAccepted
        }
      });
    }
  }, [data]);

  if (error) throw new Error(error);
  if (loading || !Object.keys(state).length)
    return <Spinner text="Fetching shop data" />;
  else if (data)
    return (
      <ShopProvider
        value={{
          state,
          setItem,
          dispatch,
          discard,
          validationErrors,
          setValidationErrors
        }}
      >
        {children}
      </ShopProvider>
    );
};
